/**
 * Holds the column and cell rules shared by the chat's `show_table` and
 * `show_chart` renderers. Both outputs carry the same server-built `columns` +
 * `rows` shape, so validating and formatting them in one place keeps a table and
 * a chart of the same query showing the same numbers.
 */
import { defaultFormatCell, formatPercent } from 'src/utils/dashboardUtils';
import { formatUtcDate } from 'src/utils/formatDateTime';

export type TableCellValue = string | number | null;

export interface TableColumn { key: string; label: string; format: string; currency?: string }

export const NUMERIC_FORMATS = new Set(['integer', 'decimal', 'percent', 'currency']);
const TABLE_FORMATS = new Set(['string', 'integer', 'decimal', 'percent', 'currency', 'date']);

export const isTableColumn = (value: unknown): value is TableColumn => {
  const column = value as Partial<TableColumn> | null;
  if (typeof column?.key !== 'string' || column.key === '' || typeof column.label !== 'string') return false;
  if (typeof column.format !== 'string' || !TABLE_FORMATS.has(column.format)) return false;
  return column.format !== 'currency' || (typeof column.currency === 'string' && /^[A-Z]{3}$/.test(column.currency));
};

export const isTableCell = (value: unknown): value is TableCellValue =>
  value === null || typeof value === 'string' || (typeof value === 'number' && Number.isFinite(value));

export interface TableResult {
  title: string;
  caption?: string;
  columns: TableColumn[];
  rows: TableCellValue[][];
  truncated?: boolean;
}

// Checks only the title, rows array and column list; each renderer adds its own column-count and row rules.
export const isTableResult = (value: unknown): value is TableResult => {
  const result = value as Partial<TableResult> | null;
  if (typeof result?.title !== 'string' || result.title.trim() === '') return false;
  if (!Array.isArray(result.columns) || !Array.isArray(result.rows)) return false;
  const columns: unknown[] = result.columns;
  if (!columns.every(isTableColumn)) return false;
  return new Set(columns.map(({ key }) => key)).size === columns.length;
};

const formatCurrency = (value: TableCellValue, currency = ''): string =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(Number(value));

export const formatTableCell = (value: TableCellValue, column: TableColumn): string => {
  if (value == null) return '--';
  if (NUMERIC_FORMATS.has(column.format) && typeof value !== 'number') return '--';
  switch (column.format) {
    case 'integer':
    case 'decimal':
      return defaultFormatCell(value, column.format);
    case 'percent':
      return formatPercent(value);
    case 'currency':
      return formatCurrency(value, column.currency);
    case 'date':
      return formatUtcDate(String(value), String(value));
    default:
      return String(value);
  }
};
